
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { DisclaimerBanner } from "../components/DisclaimerBanner";
import FilterBar from "../components/FilterBar";
import { DoctorListResults } from "../components/DoctorListResults";
import { ReportDialog } from "../components/ReportDialog";
import ProviderMap from "../components/ProviderMap";

import { fetchDoctors } from "../lib/api";
import { useMetadata } from "../lib/hooks";
import { BRANDING } from "../lib/branding";

const PAGE_SIZE = 6;

const defaultFilters = {
  q: "",
  specialty: "",
  city: "",
  status: "",
  sort_by: "confidence",
  order: "desc",
};

function getFiltersFromUrl(searchParams) {
  return {
    q: searchParams.get("q") || "",
    specialty: searchParams.get("specialty") || "",
    city: searchParams.get("city") || "",
    status: searchParams.get("status") || "",
    sort_by: searchParams.get("sort_by") || "confidence",
    order: searchParams.get("order") || "desc",
  };
}

function LoadingSkeleton() {
  return (
    <div className="mt-8">
      <div className="rounded-[2rem] bg-white border border-slate-200 shadow-lg p-5 mb-8">
        <div className="h-[280px] rounded-3xl bg-slate-100 animate-pulse" />
      </div>
    </div>
  );
}

export default function Doctors() {
  const meta = useMetadata();
  const resultsRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const [allDoctors, setAllDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportDoctor, setReportDoctor] = useState(null);

  const [appliedFilters, setAppliedFilters] = useState(() =>
    getFiltersFromUrl(searchParams)
  );

  const [currentPage, setCurrentPage] = useState(() => {
    const page = Number(searchParams.get("page") || 1);
    return Number.isFinite(page) && page > 0 ? page : 1;
  });

  const { q, specialty, city, status, sort_by, order } = appliedFilters;

  const loadDoctors = useCallback(() => {
    setLoading(true);

    fetchDoctors({ limit: 200 })
      .then((data) => {
        setAllDoctors(Array.isArray(data?.doctors) ? data.doctors : []);
      })
      .catch((error) => {
        console.error("Doctor fetch failed:", error);
        setAllDoctors([]);
      })
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    loadDoctors();
  }, [loadDoctors]);

  React.useEffect(() => {
    const nextFilters = getFiltersFromUrl(searchParams);
    setAppliedFilters(nextFilters);

    const page = Number(searchParams.get("page") || 1);
    setCurrentPage(Number.isFinite(page) && page > 0 ? page : 1);
  }, [searchParams]);

  const filteredDoctors = useMemo(() => {
    let results = [...allDoctors];

    if (q) {
      const search = q.toLowerCase();

      results = results.filter((d) =>
        `${d.name || ""} ${d.clinic_name || ""} ${d.city || ""} ${
          d.specialty || ""
        }`
          .toLowerCase()
          .includes(search)
      );
    }

    if (specialty) {
      results = results.filter((d) => {
        const providerType = String(d.provider_type || "").toLowerCase();
        const spec = String(d.specialty || "").toLowerCase();

        if (specialty === "family") {
          return providerType === "family" || spec.includes("family");
        }

        if (specialty === "walkin") {
          return (
            providerType === "walkin" ||
            spec.includes("walk-in") ||
            spec.includes("walk in")
          );
        }

        if (specialty === "primary") {
          return providerType === "primary" || spec.includes("primary");
        }

        if (specialty === "pediatrics") {
          return providerType === "pediatrics" || spec.includes("pediatric");
        }

        return providerType === specialty || spec === specialty.toLowerCase();
      });
    }

    if (city) {
      results = results.filter((d) => String(d.city || "") === city);
    }

    if (status) {
      results = results.filter((d) => String(d.status || "") === status);
    }

    results.sort((a, b) => {
      if (sort_by === "name") {
        return order === "asc"
          ? String(a.name || "").localeCompare(String(b.name || ""))
          : String(b.name || "").localeCompare(String(a.name || ""));
      }

      if (sort_by === "updated") {
        const da = new Date(a.last_updated || a.last_verified || 0);
        const db = new Date(b.last_updated || b.last_verified || 0);

        return order === "asc" ? da - db : db - da;
      }

      const ca = Number(a.confidence_score || 0);
      const cb = Number(b.confidence_score || 0);

      return order === "asc" ? ca - cb : cb - ca;
    });

    return results;
  }, [allDoctors, q, specialty, city, status, sort_by, order]);

  const total = filteredDoctors.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const doctors = filteredDoctors.slice(start, start + PAGE_SIZE);

  const scrollToResults = () => {
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 250);
  };

  const writeUrlParams = useCallback(
    (filters, page = 1) => {
      const next = new URLSearchParams();

      if (filters.q) next.set("q", filters.q);
      if (filters.specialty) next.set("specialty", filters.specialty);
      if (filters.city) next.set("city", filters.city);
      if (filters.status) next.set("status", filters.status);
      if (filters.sort_by && filters.sort_by !== "confidence") {
        next.set("sort_by", filters.sort_by);
      }
      if (filters.order && filters.order !== "desc") {
        next.set("order", filters.order);
      }
      if (page > 1) next.set("page", String(page));

      setSearchParams(next, { replace: false });
    },
    [setSearchParams]
  );

  const handleApplyFilters = useCallback(
    (nextFilters) => {
      const cleanFilters = {
        q: nextFilters.q || "",
        specialty: nextFilters.specialty || "",
        city: nextFilters.city || "",
        status: nextFilters.status || "",
        sort_by: nextFilters.sort_by || "confidence",
        order: nextFilters.order || "desc",
      };

      setAppliedFilters(cleanFilters);
      setCurrentPage(1);
      writeUrlParams(cleanFilters, 1);
      scrollToResults();
    },
    [writeUrlParams]
  );

  const clearAll = useCallback(() => {
    setAppliedFilters(defaultFilters);
    setCurrentPage(1);
    setSearchParams({}, { replace: false });
  }, [setSearchParams]);

  const handlePageChange = useCallback(
    (page) => {
      const nextPage = Math.max(1, Math.min(page, totalPages));
      setCurrentPage(nextPage);
      writeUrlParams(appliedFilters, nextPage);
      scrollToResults();
    },
    [appliedFilters, totalPages, writeUrlParams]
  );

  const hasFilters = !!(q || specialty || city || status);

  return (
    <div
      data-testid="doctors-page"
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 overflow-x-hidden"
    >
      <div className="min-w-0">
        <span className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">
          Primary Care Search
        </span>

        <h1 className="mt-2 font-bold text-3xl sm:text-5xl text-slate-900 tracking-tight leading-tight">
          Find family doctors and clinics
        </h1>

        <p className="mt-3 text-sm sm:text-base text-slate-600 max-w-2xl">
          Choose a provider category, city, and availability status. Results
          update only after clicking Apply Filters.
        </p>

        <p className="mt-2 text-sm text-slate-500">
          Showing{" "}
          <span className="font-semibold text-slate-800">{doctors.length}</span>{" "}
          of <span className="font-semibold text-slate-800">{total}</span>{" "}
          provider{total === 1 ? "" : "s"}
          {BRANDING.demoLabel ? ` · ${BRANDING.demoLabel}` : ""}
        </p>
      </div>

      <div className="relative lg:sticky lg:top-3 z-30 mt-6 sm:mt-8 bg-white/95 backdrop-blur-xl rounded-[1.5rem] sm:rounded-[2rem] border border-white shadow-xl p-4 sm:p-5 overflow-hidden">
        <FilterBar
          meta={meta}
          values={appliedFilters}
          onApply={handleApplyFilters}
          onClear={clearAll}
          hasFilters={hasFilters}
        />
      </div>

      <div className="mt-5 sm:mt-6">
        <DisclaimerBanner variant="inline" />
      </div>

      <div
        ref={resultsRef}
        id="doctor-results"
        className="mt-6 sm:mt-8 scroll-mt-28"
      >
        {loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <ProviderMap doctors={filteredDoctors} />

            <DoctorListResults
              loading={loading}
              doctors={doctors}
              query={q}
              onClear={clearAll}
              onReport={setReportDoctor}
              currentPage={safePage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              hasSearched={true}
            />
          </>
        )}
      </div>

      <ReportDialog
        open={!!reportDoctor}
        onOpenChange={(o) => {
          if (!o) setReportDoctor(null);
        }}
        doctor={reportDoctor}
        onSubmitted={() => {
          setReportDoctor(null);
          loadDoctors();
        }}
      />
    </div>
  );
}